import { writeClipboardText } from './chunk-TNAHBB5Q.js';
import { Separator } from './chunk-NCCYLVD3.js';
import { Button } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Check, Copy } from 'lucide-react';
import { formatShare } from '@authe/fe-commons/value';
import { formatDuration, formatTimestamp, formatRelativeTime } from '@authe/fe-commons/time';
import { customerData } from '@authe/fe-commons/evidence';
import { isDebugEnabled } from '@authe/fe-commons/config';

function keepEvidenceActivationLocal(event) {
  if (event.key === " " || event.key === "Enter") event.stopPropagation();
}
function EvidenceDisclosure({
  summary,
  children,
  expanded = false,
  lazy = false,
  className,
  summaryClassName,
  contentClassName,
  label
}) {
  const [open, setOpen] = React.useState(expanded);
  React.useEffect(() => setOpen(expanded), [expanded]);
  return /* @__PURE__ */ jsxs("details", { className, open, onToggle: (event) => setOpen(event.currentTarget.open), children: [
    /* @__PURE__ */ jsx(
      "summary",
      {
        "aria-label": label,
        className: cn("cursor-pointer rounded font-medium focus-ring", summaryClassName),
        onKeyDown: keepEvidenceActivationLocal,
        onKeyUp: keepEvidenceActivationLocal,
        children: summary
      }
    ),
    (!lazy || open) && /* @__PURE__ */ jsx("div", { className: cn("min-w-0 space-y-2 [overflow-wrap:anywhere]", contentClassName), children })
  ] });
}
function useCopy(text, ms = 1e3) {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    void writeClipboardText(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      window.setTimeout(() => setCopied(false), ms);
    });
  }, [text, ms]);
  return [copied, copy];
}
function CopyableValue({ value }) {
  const [copied, copy] = useCopy(value);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: copy,
      title: `Copy: ${value}`,
      className: "inline-flex max-w-full items-center gap-1 font-mono hover:text-foreground",
      children: [
        /* @__PURE__ */ jsx("span", { className: "truncate", children: value }),
        copied ? /* @__PURE__ */ jsx(Check, { className: "size-3 shrink-0 text-success", "aria-hidden": true }) : /* @__PURE__ */ jsx(Copy, { className: "size-3 shrink-0 opacity-50", "aria-hidden": true })
      ]
    }
  );
}
function CopyTextButton({
  text,
  label = "Copy"
}) {
  const [copied, copy] = useCopy(text, 1200);
  return /* @__PURE__ */ jsx(
    Button,
    {
      type: "button",
      size: "sm",
      variant: "ghost",
      className: "text-xs",
      onClick: copy,
      disabled: !text,
      children: copied ? "Copied" : label
    }
  );
}
function toUnixSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e11 ? value / 1e3 : value;
  }
  if (typeof value === "string" && value !== "") {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) return ms / 1e3;
  }
  return null;
}
function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return String(bytes);
  if (Math.abs(bytes) < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (Math.abs(value) >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(Math.abs(value) < 100 ? 1 : 0)} ${units[unit]}`;
}
function isAbsent(value) {
  return value === null || value === void 0 || value === "";
}
function isEmptyContainer(value) {
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
function FieldValue({ field }) {
  const { value, kind } = field;
  if (isAbsent(value)) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
  switch (kind) {
    case "id":
      return /* @__PURE__ */ jsx(CopyableValue, { value: String(value) });
    case "timestamp": {
      const seconds = toUnixSeconds(value);
      if (seconds === null) return /* @__PURE__ */ jsx(Fragment, { children: String(value) });
      return /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-col items-end leading-tight", children: [
        /* @__PURE__ */ jsx("span", { children: formatTimestamp(seconds) }),
        /* @__PURE__ */ jsx("span", { className: "text-3xs text-muted-foreground", children: formatRelativeTime(seconds) })
      ] });
    }
    case "duration_ms":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatDuration(value) : String(value) });
    case "bytes":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatBytes(value) : String(value) });
    case "ratio":
      return /* @__PURE__ */ jsx(Fragment, { children: typeof value === "number" ? formatShare(value) : String(value) });
    case "json": {
      if (isEmptyContainer(value)) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "none" });
      return /* @__PURE__ */ jsx("pre", { className: "max-h-32 max-w-full overflow-auto whitespace-pre-wrap break-all rounded-sm border bg-muted/40 p-1.5 text-left text-3xs leading-relaxed", children: JSON.stringify(value, null, 2) });
    }
    default:
      return /* @__PURE__ */ jsx(Fragment, { children: String(value) });
  }
}
function DebugFieldRow({ field }) {
  const absent = isAbsent(field.value);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
    /* @__PURE__ */ jsx("span", { className: "shrink-0 text-muted-foreground", title: field.hint, children: field.label }),
    /* @__PURE__ */ jsxs("span", { className: "min-w-0 max-w-[62%] break-words text-right font-mono text-foreground", children: [
      /* @__PURE__ */ jsx(FieldValue, { field }),
      absent && field.hint && /* @__PURE__ */ jsx("span", { className: "block text-3xs font-sans italic text-muted-foreground", children: field.hint })
    ] })
  ] });
}
function DebugFieldGroup({ group }) {
  if (group.fields.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsx("p", { className: "text-2xs font-medium uppercase tracking-wide text-muted-foreground", children: group.label }),
    group.fields.map((field, i) => /* @__PURE__ */ jsx(DebugFieldRow, { field }, `${field.key}-${i}`))
  ] });
}
function DebugFieldGroups({ groups }) {
  const nonEmpty = groups.filter((g) => g.fields.length > 0);
  if (nonEmpty.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: "space-y-3", children: nonEmpty.map((group, i) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
    i > 0 && /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsx(DebugFieldGroup, { group })
  ] }, `${group.key}-${i}`)) });
}
function DebugDetails({ summary = "Technical details", groups, children, className }) {
  if (!isDebugEnabled() || !children && !groups?.some((group) => group.fields.length)) return null;
  return /* @__PURE__ */ jsxs(
    EvidenceDisclosure,
    {
      summary,
      lazy: true,
      className,
      summaryClassName: "text-xs text-muted-foreground",
      contentClassName: "pt-2 text-xs",
      children: [
        groups && /* @__PURE__ */ jsx(DebugFieldGroups, { groups: customerData(groups) }),
        children
      ]
    }
  );
}

export { CopyTextButton, CopyableValue, DebugDetails, DebugFieldGroup, DebugFieldGroups, EvidenceDisclosure, keepEvidenceActivationLocal, useCopy };
//# sourceMappingURL=chunk-XKA7GV2U.js.map
//# sourceMappingURL=chunk-XKA7GV2U.js.map